import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import DateRangeScanner from "@/components/DateRangeScanner";

function md(text) {
  return text.split("\n").map((l, i) => {
    const k = `L${i}-${l.slice(0, 24)}`;
    if (l.startsWith("## ")) return <h3 key={k} className="font-heading text-xl text-[#D4AF37] mt-6 mb-2">{l.slice(3)}</h3>;
    if (l.startsWith("# ")) return <h2 key={k} className="font-heading text-2xl text-[#E6E1D6] mt-6 mb-2">{l.slice(2)}</h2>;
    if (l.startsWith("- ") || l.startsWith("* ")) return <li key={k} className="text-[#E6E1D6] ml-6 list-disc">{l.slice(2)}</li>;
    if (!l.trim()) return <div key={k} className="h-2" />;
    return <p key={k} className="text-[#E6E1D6] leading-relaxed font-light">{l}</p>;
  });
}

const sevColor = (s) => s >= 26 ? "#C15C3D" : s >= 20 ? "#E5C158" : "#A8896C";

export default function RedAlertsPanel({ profileId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.redAlerts(profileId)); }
    catch (e) {
      console.error("red alerts failed:", e);
      toast.error(e?.response?.data?.detail || "Failed to load red alerts");
    }
    setLoading(false);
  }, [profileId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-[#A19E95] font-mono text-sm">Scanning 90-year dasha for malefic loading… (~60s first time)</div>;
  if (!data) return null;
  const alerts = data.alerts || [];

  return (
    <div className="space-y-6" data-testid="red-alerts-panel">
      <DateRangeScanner profileId={profileId} kind="red" />
      <div className="flex items-center gap-2 border-l-4 border-[#C15C3D] pl-3">
        <AlertTriangle size={20} className="text-[#C15C3D]" />
        <div>
          <div className="text-xs uppercase tracking-widest text-[#C15C3D] font-mono">Red-Alert Dasha Windows</div>
          <div className="text-[10px] text-[#6E6B64] font-mono">Maraka + malefic + 6/8/12 house lord loading during Pratyantar dashas · top {alerts.length} by severity</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {alerts.map((a) => (
          <div key={`${a.start}-${a.chain.join('-')}`}
               className="border p-3 bg-[#181A1F]"
               style={{ borderColor: sevColor(a.severity_score) }}
               data-testid={`redalert-${a.start}`}>
            <div className="flex items-baseline justify-between">
              <div className="font-mono text-xs text-[#A19E95]">
                {a.start} → {a.end}
              </div>
              <div className="font-mono text-xs font-bold" style={{ color: sevColor(a.severity_score) }}>
                {a.severity_score}
              </div>
            </div>
            <div className="mt-1 font-heading text-sm text-[#E6E1D6]">
              {a.chain.join(" › ")}
            </div>
          </div>
        ))}
      </div>

      {data.narrative && (
        <div className="border border-[#2A2D35] bg-[#181A1F] p-6" data-testid="red-alerts-narrative">
          {md(data.narrative)}
        </div>
      )}
    </div>
  );
}
